/* eslint-disable @angular-eslint/use-lifecycle-interface */
import { Component, Input, HostListener, OnDestroy, Inject } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { CommonModule } from '@angular/common';
import { HeaderComponent } from '@shared/ui/header/header.component';
import { UnloggedHeaderComponent } from '@shared/ui/unlogged-header/unlogged-header.component';
import { filter, Subject, takeUntil } from 'rxjs';
import { ToastComponent } from '@shared/ui/toast/toast.component';
import { ModalComponent } from '@shared/ui/modal/modal.component';
import { AuthService } from '@core/auth/auth.service';
import { ModalService } from '@core/services/modal/modal.service';
import { LanguageService } from '@core/i18n/language.service';
import { LoadingComponent } from '@shared/ui/loading/loading.component';
import { SocketSwitchService } from '@core/realtime/socketswitch.service';

@Component({
  selector: 'app-root',
  imports: [
    CommonModule,
    RouterOutlet,
    LoadingComponent,
    HeaderComponent,
    UnloggedHeaderComponent,
    ToastComponent,
    ModalComponent,
  ],
  templateUrl: './app.html',
  styleUrl: './app.scss',
})
export class App implements OnDestroy {
  headerType = '';
  listValue = false;
  disablePadding = false;
  scrollable = false;
  // Public/token-less routes (e.g. sign-up) opt out of the app header. The header mounts the
  // sidebar and user-menu, which eagerly load permissions/infrastructures — authenticated calls
  // that 401 without a token and bounce the visitor to Keycloak. Chromeless auth pages skip it.
  hideHeader = false;
  // Chromeless pages (e.g. consent) render no header at all, neither logged nor unlogged.
  noHeader = false;
  private readonly destroy$ = new Subject<void>();
  private sessionTranslations: Record<string, string> = {};

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthService,
    private modalService: ModalService,
    private langService: LanguageService,
    private socketSwitch: SocketSwitchService,
  ) {}

  ngOnInit() {
    this.updateRouteData();

    this.langService
      .getSharedDictionary('session')
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        this.sessionTranslations = data;
      });

    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      this.updateRouteData();
    });

    this.socketSwitch.forceLogout$
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ user_id }: { user_id: number }) => {
        const currentId = Number(this.authService.getUserId());
        if (currentId && currentId === user_id) {
          this.modalService
            .warning(
              () =>
                this.sessionTranslations['expired_detail'] ||
                'Your session has expired because your account permissions have been modified.',
              () => this.sessionTranslations['expired_title'] || 'Session Expired',
              { closable: false, backdropClose: false },
            )
            .subscribe(() => this.authService.logout());
        }
      });
  }

  private updateRouteData() {
    let route = this.route.snapshot;
    while (route.firstChild) {
      route = route.firstChild;
    }

    this.headerType = route.data['headerType'] ?? '';
    this.listValue = route.data['listValue'];
    this.disablePadding = route.data['disablePadding'];
    this.scrollable = route.data['scrollable'];
    this.hideHeader = route.data['hideHeader'] ?? false;
    this.noHeader = route.data['noHeader'] ?? false;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
